import type { MouseEventHandler, ReactNode, RefObject, SyntheticEvent } from 'react';
import { NodeOverlay } from './NodeOverlay';
import { RetoolingOverflow } from './RetoolingOverflow';
import { StyledNodeBase, type StyledNodeBaseProps } from './StyledNodeBase';
import type { DraggableAttributes } from '@dnd-kit/core/dist/hooks/useDraggable';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { LocateProductionTask } from '../../hooks';

export type NodeBaseProps = StyledNodeBaseProps & {
  children: ReactNode;
  width: number;
  retoolingWidth?: number;
  id?: number;
  listeners?: SyntheticListenerMap;
  attributes?: DraggableAttributes;
  onClick?: MouseEventHandler;
  onMouseDown?: (e: SyntheticEvent) => void;
  onMouseEnter?: MouseEventHandler;
  onMouseMove?: MouseEventHandler;
  onMouseLeave?: MouseEventHandler;
  isDraggable?: boolean;
  className?: string;
  /**
   * nodeIds are used for useLocateGanttTask hook
   */
  nodeIds?: Array<string | number>;
  ref?: RefObject<HTMLDivElement>;
};

export const NodeBase = ({
  children,
  width,
  isDisabled = false,
  isEmphasized = false,
  isGraphHighlight = false,
  isRetooling = false,
  retoolingWidth = 0,
  hasError = false,
  isSmall = false,
  isSelected = false,
  isSearchSelected = false,
  isGraph = false,
  isSquared = false,
  isDragging,
  listeners,
  attributes,
  onClick,
  onMouseDown,
  isDraggable,
  isSelectable = true,
  nodeIds,
  unassigned,
  isOverdue,
  className,
  style,
  shadowParams,
  toPublish,
  warehouseShortage,
  ref,
  ...props
}: NodeBaseProps) => {
  const graphIdsString = nodeIds?.reduce((acc, id) => {
    return { ...acc, [`data-deps-graph-${id}`]: true };
  }, {});

  return (
    <>
      <StyledNodeBase
        ref={ref}
        width={width}
        {...attributes}
        {...listeners}
        {...graphIdsString}
        className={className}
        draggable={!!isDraggable}
        hasError={hasError}
        isDisabled={isDisabled}
        isDragged={false}
        isDragging={isDragging}
        isEmphasized={isEmphasized}
        isGraph={isGraph}
        isGraphHighlight={isGraphHighlight}
        isOverdue={isOverdue}
        isRetooling={isRetooling}
        isSearchSelected={isSearchSelected}
        isSelectable={isSelectable}
        isSelected={isSelected}
        isSmall={isSmall}
        isSquared={isSquared}
        shadowParams={shadowParams}
        style={style}
        toPublish={toPublish}
        unassigned={unassigned}
        warehouseShortage={warehouseShortage}
        onClick={onClick}
        onMouseDown={onMouseDown}
        {...props}
      >
        {nodeIds?.map((id, index) => (
          <span className="invisible absolute" key={`${id}-${index}`} {...LocateProductionTask.addNodeSelector(id)} />
        ))}
        {isRetooling && retoolingWidth > 0 && <RetoolingOverflow width={retoolingWidth} />}
        <div className="z-1 w-full">{children}</div>
      </StyledNodeBase>
      {isDragging && (
        <NodeOverlay>
          <StyledNodeBase
            isDragged
            hasError={hasError}
            isDisabled={false}
            isEmphasized={isEmphasized}
            isGraphHighlight={isGraphHighlight}
            isOverdue={isOverdue}
            isRetooling={isRetooling}
            isSelectable={isSelectable}
            isSmall={isSmall}
            isSquared={isSquared}
            width={width}
          >
            {children}
            {isRetooling && retoolingWidth > 0 && <RetoolingOverflow width={retoolingWidth} />}
          </StyledNodeBase>
        </NodeOverlay>
      )}
    </>
  );
};

NodeBase.displayName = 'NodeBase';
